import { useMemo } from 'react';

import { flatMap } from 'lodash';

import type {
  PrivateSessionDrawerMetadataQuery_EnterpriseProductConfigurationMetadataV1Resource_get_metadataType_EnterpriseProductConfigurationMetadataV1_courseMember_course as CourseMetadata,
  PrivateSessionDrawerMetadataQuery_EnterpriseProductConfigurationMetadataV1Resource_get_metadataType_EnterpriseProductConfigurationMetadataV1_specializationMember_specialization as S12nMetadata,
} from 'bundles/enterprise-admin-drawers/components/__generated__/PrivateSessionDrawerMetadataQuery';
import type { InjectedNaptime } from 'bundles/naptimejs';
import { useNaptime } from 'bundles/naptimejs';
import EnterpriseProductConfigurations from 'bundles/naptimejs/resources/EnterpriseProductConfigurations.v1';
import type {
  EnterpriseAdminSessionSelectionOptionTypeExternallyManagedSessionOption,
  EnterpriseAdminSessionSelectionOptionTypePrivateSessionGroupOption,
  EnterpriseProductConfigurationTypeCourse,
  EnterpriseProductConfigurationsV1,
} from 'bundles/naptimejs/resources/__generated__/EnterpriseProductConfigurationsV1';
import OnDemandCourseSessionsV1 from 'bundles/naptimejs/resources/onDemandCourseSessions.v1';

type Props = {
  metadata?: CourseMetadata | S12nMetadata;
  isS12n: boolean;
  productId?: string;
  programId?: string;
  shouldWaitToQuery?: boolean;
};

type PropsFromNaptime1 = {
  productConfig?: EnterpriseProductConfigurationsV1;
  initialSessions?: Array<OnDemandCourseSessionsV1>;
};

type PropsFromNaptime2 = {
  courseSpecificSessions?: Array<OnDemandCourseSessionsV1>;
};

export type PropsFromUseSessionInfo = {
  productConfig?: EnterpriseProductConfigurationsV1;
  sessions?: OnDemandCourseSessionsV1[];
  isSessionInfoLoading?: boolean;
  naptime: InjectedNaptime;
};

const useSessionInfo = ({ metadata, isS12n, programId, productId, shouldWaitToQuery }: Props) => {
  const associatedSessionId = metadata && 'associatedSessionId' in metadata ? metadata.associatedSessionId : null;

  const naptimeResponse = useNaptime<PropsFromNaptime1>(() => {
    if (!programId || !productId || shouldWaitToQuery) {
      return {};
    } else if (associatedSessionId) {
      return {
        productConfig: EnterpriseProductConfigurations.get(`Program!~${programId}~Specialization!~${productId}`, {
          fields: ['productConfigurationType'],
        }),
        initialSessions: OnDemandCourseSessionsV1.multiGet([associatedSessionId], {
          fields: ['courseId', 'endsAt', 'startsAt', 'name'],
        }),
      };
      // A s12n without a configured session yet (either unconfigured or a flexible session)
    } else if (isS12n) {
      return {
        productConfig: EnterpriseProductConfigurations.get(`Program!~${programId}~Specialization!~${productId}`, {
          fields: ['productConfigurationType'],
        }),
        initialSessions: undefined,
      };
    } else {
      // Only for courses
      return {
        productConfig: EnterpriseProductConfigurations.get(`Program!~${programId}~VerifiedCertificate!~${productId}`, {
          includes: ['courseSessions'],
          fields: ['productConfigurationType', 'onDemandCourseSessions.v1(startsAt,endsAt,courseId)'],
        }),
      };
    }
  }, [shouldWaitToQuery]);

  const {
    data: { productConfig, initialSessions },
    pending: isFirstNaptimeResponseLoading,
    naptime,
  } = naptimeResponse;

  const courseSessionOptions = useMemo(() => {
    const courseOptions = (
      productConfig?.productConfigurationType as EnterpriseProductConfigurationTypeCourse
    )?.definition?.selectableSessions
      ?.filter((session) => session.optionType.typeName !== 'flexibleOption')
      .map((session) => session.optionType) as (
      | EnterpriseAdminSessionSelectionOptionTypePrivateSessionGroupOption
      | EnterpriseAdminSessionSelectionOptionTypeExternallyManagedSessionOption
    )[];

    return flatMap(courseOptions, (o) => o.definition.sessionId);
  }, [productConfig?.productConfigurationType]);

  const courseSessionsNaptimeResponse = useNaptime<PropsFromNaptime2>(() => {
    if (isS12n || isFirstNaptimeResponseLoading) {
      return {};
    } else if (!courseSessionOptions?.length) {
      return {
        courseSpecificSessions: undefined,
      };
    } else {
      return {
        courseSpecificSessions: OnDemandCourseSessionsV1.multiGet(courseSessionOptions),
      };
    }
  }, [shouldWaitToQuery, isFirstNaptimeResponseLoading]);

  const {
    data: { courseSpecificSessions },
    pending: areCourseSessionsLoading,
  } = courseSessionsNaptimeResponse;

  return {
    productConfig,
    sessions: isS12n ? initialSessions : courseSpecificSessions,
    isSessionInfoLoading: areCourseSessionsLoading || isFirstNaptimeResponseLoading,
    naptime,
  };
};

export default useSessionInfo;
